<template>
  <div class="grid-page-chart-editor">
    <div class="_top">
      <div class="_left">
        <breadcrumbs
          class="_breadcrumbs"
          :breadcrumbs="breadcrumbItems"
        />
        <div class="_divider">
          /
        </div>
        <m-dropdown
          :title="$t('general.actions')"
          class="_title"
        >
          <m-btn
            type="clickable"
            hide-border
            small
          >
            {{ maxLength(props.gridPageTile.title, 30) }}
          </m-btn>
          <template #overlay>
            <m-card
              padding-xxs
              :style="{ width: '20rem'}"
            >
              <m-focusable>
                <simple-editor
                  auto-focus
                  :initial-value="props.gridPageTile.title"
                  :read-only="false"
                  auto-select
                  :placeholder="$t('gridPageChartEditor.titlePlaceholder')"
                  @update:value="updateTitle"
                />
              </m-focusable>
            </m-card>
          </template>
        </m-dropdown>
        <m-dropdown
          v-model:value="descriptionEditMode"
          :style="{ marginLeft: '.6rem' }"
          :title="$t('general.actions')"
          class="_description"
        >
          <m-btn
            type="clickable"
            small
            light
            :icon="props.gridPageTile.description === '' ? 'plus' : undefined"
            hide-border
          >
            <template v-if="props.gridPageTile.description !== ''">
              {{ maxLength(props.gridPageTile.description, 50) }}
            </template>
            <template v-else>
              {{ $t('gridPageChartEditor.addDescription') }}
            </template>
          </m-btn>
          <template #overlay>
            <m-card
              padding-xxs
              :style="{ width: '30rem'}"
            >
              <m-focusable>
                <simple-editor
                  :style="{ width: '100%' }"
                  :initial-value="props.gridPageTile.description"
                  :read-only="false"
                  auto-select
                  auto-focus
                  :placeholder="$t('gridPageChartEditor.descriptionPlaceholder')"
                  :enter-handler="updateDescription"
                  @update:value="updateDescription"
                  @enter="descriptionEditMode = false"
                />
              </m-focusable>
            </m-card>
          </template>
        </m-dropdown>
      </div>
      <div class="_right">
        <template
          v-if="isDirty"
        >
          <m-btn
            class="_discard-btn"
            hide-border
            light
            @click="discard"
          >
            {{ $t('general.discardChanges') }}
          </m-btn>
          <m-btn
            color="primary"
            @click="save"
          >
            {{ $t('general.save') }}
          </m-btn>
        </template>
        <m-btn
          hide-border
          light
          icon="close"
          fab
          @click="handleClose"
        />
      </div>
    </div>
    <div class="_bottom">
      <div class="_left">
        <full-screen-spinner v-if="chartDataQuery.loading.value" />
        <component
          :is="effectiveChartType !== gridPageChartType.table ? DraggableLayout : 'div'"
          v-else
          v-model:position="tablePosition"
          class="_draggable-layout"
        >
          <div
            :class="['_chart-container', { '-table': effectiveChartType === gridPageChartType.table }]"
          >
            <m-content
              class="_chart-content"
              :padding-x="12"
              :padding-y="10"
            >
              <grid-page-chart
                :chart="toSave"
                :chart-data-query="chartDataQuery"
                size="large"
              />
            </m-content>
          </div>
          <template #bottom>
            <m-content
              :padding-x="12"
              :padding-top="0"
              :padding-bottom="10"
              class="_table-container"
            >
              <grid-page-chart-data-table
                :chart="toSave"
                :chart-data-query="chartDataQuery"
              />
            </m-content>
          </template>
        </component>
      </div>
      <div class="_right">
        <div
          v-if="source === goalConfig.model"
          class="_time-mode-tabs"
        >
          <m-tab
            v-for="option in timeModeOptions"
            :key="option.value"
            :title="option.text"
            :active="timeMode === option.value"
            @click="timeMode = option.value"
          />
        </div>
        <div class="_inner">
          <div class="_section-header">
            {{ $t('gridPageChartEditor.title') }}
          </div>
          <m-select
            :items="sources"
            :value="source"
            value-key="value"
            item-text="text"
            match-trigger-width
            full-width
            @input="val => source = val"
          >
            <template #trigger>
              <m-card-item icon="database">
                {{ $t('gridPageChartEditor.source') }}
                <template #right>
                  <chart-editor-setting-value :value="sourceDisplayValue" />
                </template>
              </m-card-item>
            </template>
          </m-select>
          <m-dropdown
            block
            placement="bottomRight"
            :relocate-key="filter"
            :title="$t('gridPageChartEditor.filter')"
          >
            <m-card-item icon="funnel">
              {{ $t('gridPageChartEditor.filter') }}
              <template #right>
                <chart-editor-setting-value :value="filter?.children.length > 0 ? filter.children.length : ''" />
              </template>
            </m-card-item>
            <template #overlay>
              <m-card
                padding-xs
                :style="{ minWidth: '30rem' }"
              >
                <scope-filter
                  v-model:value="filter"
                  can-remove-all
                  allow-grouping
                  :props="filterProps"
                  :account="loggedInUserAccount"
                />
              </m-card>
            </template>
          </m-dropdown>
          <goal-cycle-selector
            v-if="source === goalConfig.model"
            v-model:value="selectedCycles"
            keep-open-on-click
            multiple
            full-width
          >
            <template #trigger="{ value: cycles }">
              <m-card-item icon="retweet">
                {{ $t('gridPageChartEditor.cycle') }}
                <template #right>
                  <chart-editor-setting-value :value="cycles.length === 1 ? cycles[0].title : cycles.length > 1 ? $t('gridPageChartEditor.cycleCount', { count: cycles.length }) : ''" />
                </template>
              </m-card-item>
            </template>
          </goal-cycle-selector>
          <div class="_section-header">
            {{ $t('gridPageChartEditor.chart') }}
          </div>
          <div class="_chart-type-selector">
            <m-tooltip
              v-for="type in visibleChartTypes"
              :key="type"
            >
              <m-focusable
                type="clickable"
                :class="{ '_chart-btn': true, '-selected': effectiveChartType === type }"
                :hide-hover="effectiveChartType === type"
                @click="chartType = type"
              >
                <div class="_icon-wrapper">
                  <m-icon
                    :type="getIconByType(type)"
                    size="24"
                    :color="effectiveChartType === type ? $colors.blue.base : $colors.grey.lighten1"
                  />
                </div>
              </m-focusable>
              <template #title>
                {{ $t(`gridPageChartEditor.${type}`) }}
              </template>
            </m-tooltip>
          </div>
          <m-alert
            v-if="!canGroupByUser && source === goalConfig.model"
            type="info"
            class="_user-grouping-hint"
            hide-border
          >
            {{ $t('gridPageChartEditor.userGroupingDisabled') }}
          </m-alert>
          <!-- X axis / Y axis sections for bar, column, line, table -->
          <template v-if="[gridPageChartType.bar, gridPageChartType.column, gridPageChartType.line, gridPageChartType.table].includes(effectiveChartType)">
            <!-- X axis (always first) -->
            <div class="_section-header">
              {{ effectiveChartType === gridPageChartType.table ? $t('gridPageChartEditor.columns') : $t('gridPageChartEditor.xAxis') }}
            </div>
            <template v-if="categoryIsXAxis">
              <!-- Category content on X axis (column, line, table) -->
              <template v-if="isTimeSeriesMode">
                <m-select
                  :items="timeSeriesIntervalOptions"
                  :value="selectedInterval"
                  value-key="value"
                  item-text="text"
                  match-trigger-width
                  full-width
                  @input="val => selectedInterval = val"
                >
                  <template #trigger>
                    <m-card-item icon="calendar">
                      {{ $t('gridPageChartEditor.interval') }}
                      <template #right>
                        <chart-editor-setting-value :value="intervalDisplayValue" />
                      </template>
                    </m-card-item>
                  </template>
                </m-select>
              </template>
              <template v-else>
                <group-by-selector
                  v-model:value="xAxisGroupBy"
                  :properties="properties"
                  :hidden-group-by-options="[chartGroupByOption.unique]"
                >
                  <template #trigger="{ displayValue }">
                    <m-card-item icon="corner-down-right">
                      {{ $t('gridPageChartEditor.whatToShow') }}
                      <template #right>
                        <chart-editor-setting-value :value="displayValue" />
                      </template>
                    </m-card-item>
                  </template>
                </group-by-selector>
                <bucket-selector
                  v-if="xAxisGroupBy.groupBy === chartGroupByOption.bucket"
                  v-model:value="customBucket"
                >
                  <template #trigger="{ displayValue }">
                    <m-card-item icon="toggle-center">
                      {{ $t('gridPageChartEditor.bucket') }}
                      <template #right>
                        <chart-editor-setting-value :value="displayValue" />
                      </template>
                    </m-card-item>
                  </template>
                </bucket-selector>
                <m-card-item
                  v-if="showExcludeNullValues"
                  icon="eye-invisible"
                  @click="toggleExcludeNullValues"
                >
                  {{ $t('gridPageChartEditor.excludeNullValues') }}
                  <template #right>
                    <m-switch
                      :value="excludeNullValues"
                      small
                    />
                  </template>
                </m-card-item>
              </template>
            </template>
            <template v-else>
              <!-- Metric content on X axis (bar) -->
              <aggregation-selector
                v-model:value="yAxisGroupBy"
                :properties="properties"
              >
                <template #trigger="{ displayValue }">
                  <m-card-item icon="corner-down-right">
                    {{ $t('gridPageChartEditor.whatToShow') }}
                    <template #right>
                      <chart-editor-setting-value :value="displayValue" />
                    </template>
                  </m-card-item>
                </template>
              </aggregation-selector>
              <template v-if="!isTimeSeriesMode">
                <group-by-selector
                  v-model:value="stackOptions"
                  :properties="properties"
                  show-none
                  :hidden-group-by-options="[chartGroupByOption.unique]"
                >
                  <template #trigger="{ displayValue }">
                    <m-card-item icon="toggle-center">
                      {{ $t('gridPageChartEditor.groupBy') }}
                      <template #right>
                        <chart-editor-setting-value :value="displayValue" />
                      </template>
                    </m-card-item>
                  </template>
                </group-by-selector>
                <bucket-selector
                  v-if="stackOptions.groupBy === chartGroupByOption.bucket"
                  v-model:value="stackCustomBucket"
                >
                  <template #trigger="{ displayValue }">
                    <m-card-item icon="toggle-center">
                      {{ $t('gridPageChartEditor.bucket') }}
                      <template #right>
                        <chart-editor-setting-value :value="displayValue" />
                      </template>
                    </m-card-item>
                  </template>
                </bucket-selector>
              </template>
            </template>

            <!-- Y axis (always second) -->
            <div class="_section-header">
              {{ effectiveChartType === gridPageChartType.table ? $t('gridPageChartEditor.values') : $t('gridPageChartEditor.yAxis') }}
            </div>
            <template v-if="categoryIsXAxis">
              <!-- Metric content on Y axis (column, line, table) -->
              <template v-if="isTimeSeriesMode">
                <m-select
                  :items="timeSeriesMetricOptions"
                  :value="yAxisGroupBy?.aggregator"
                  value-key="value"
                  item-text="text"
                  match-trigger-width
                  full-width
                  @input="val => yAxisGroupBy = { aggregator: val }"
                >
                  <template #trigger>
                    <m-card-item icon="corner-left-up">
                      {{ $t('gridPageChartEditor.whatToShow') }}
                      <template #right>
                        <chart-editor-setting-value :value="timeSeriesMetricDisplayValue" />
                      </template>
                    </m-card-item>
                  </template>
                </m-select>
              </template>
              <template v-else>
                <aggregation-selector
                  v-model:value="yAxisGroupBy"
                  :properties="properties"
                >
                  <template #trigger="{ displayValue }">
                    <m-card-item icon="corner-left-up">
                      {{ $t('gridPageChartEditor.whatToShow') }}
                      <template #right>
                        <chart-editor-setting-value :value="displayValue" />
                      </template>
                    </m-card-item>
                  </template>
                </aggregation-selector>
                <group-by-selector
                  v-model:value="stackOptions"
                  :properties="properties"
                  show-none
                  :hidden-group-by-options="[chartGroupByOption.unique]"
                >
                  <template #trigger="{ displayValue }">
                    <m-card-item icon="toggle-center">
                      {{ $t('gridPageChartEditor.groupBy') }}
                      <template #right>
                        <chart-editor-setting-value :value="displayValue" />
                      </template>
                    </m-card-item>
                  </template>
                </group-by-selector>
                <bucket-selector
                  v-if="stackOptions.groupBy === chartGroupByOption.bucket"
                  v-model:value="stackCustomBucket"
                >
                  <template #trigger="{ displayValue }">
                    <m-card-item icon="toggle-center">
                      {{ $t('gridPageChartEditor.bucket') }}
                      <template #right>
                        <chart-editor-setting-value :value="displayValue" />
                      </template>
                    </m-card-item>
                  </template>
                </bucket-selector>
              </template>
            </template>
            <template v-else>
              <!-- Category content on Y axis (bar) -->
              <group-by-selector
                v-model:value="xAxisGroupBy"
                :properties="properties"
                :hidden-group-by-options="[chartGroupByOption.unique]"
              >
                <template #trigger="{ displayValue }">
                  <m-card-item icon="corner-left-up">
                    {{ $t('gridPageChartEditor.whatToShow') }}
                    <template #right>
                      <chart-editor-setting-value :value="displayValue" />
                    </template>
                  </m-card-item>
                </template>
              </group-by-selector>
              <bucket-selector
                v-if="xAxisGroupBy.groupBy === chartGroupByOption.bucket"
                v-model:value="customBucket"
              >
                <template #trigger="{ displayValue }">
                  <m-card-item icon="toggle-center">
                    {{ $t('gridPageChartEditor.bucket') }}
                    <template #right>
                      <chart-editor-setting-value :value="displayValue" />
                    </template>
                  </m-card-item>
                </template>
              </bucket-selector>
              <m-card-item
                v-if="showExcludeNullValues"
                icon="eye-invisible"
                @click="toggleExcludeNullValues"
              >
                {{ $t('gridPageChartEditor.excludeNullValues') }}
                <template #right>
                  <m-switch
                    :value="excludeNullValues"
                    small
                  />
                </template>
              </m-card-item>
            </template>
          </template>
          <!-- Pie chart: Data section -->
          <template v-if="effectiveChartType === gridPageChartType.pie">
            <div class="_section-header">
              {{ $t('gridPageChartEditor.metricSection') }}
            </div>
            <group-by-selector
              v-model:value="xAxisGroupBy"
              :properties="properties"
              :hidden-group-by-options="[chartGroupByOption.unique]"
            >
              <template #trigger="{ displayValue }">
                <m-card-item icon="corner-down-right">
                  {{ $t('gridPageChartEditor.whatToShow') }}
                  <template #right>
                    <chart-editor-setting-value :value="displayValue" />
                  </template>
                </m-card-item>
              </template>
            </group-by-selector>
            <bucket-selector
              v-if="xAxisGroupBy.groupBy === chartGroupByOption.bucket"
              v-model:value="customBucket"
            >
              <template #trigger="{ displayValue }">
                <m-card-item icon="toggle-center">
                  {{ $t('gridPageChartEditor.bucket') }}
                  <template #right>
                    <chart-editor-setting-value :value="displayValue" />
                  </template>
                </m-card-item>
              </template>
            </bucket-selector>
            <aggregation-selector
              v-model:value="yAxisGroupBy"
              :properties="properties"
            >
              <template #trigger="{ displayValue }">
                <m-card-item icon="pie-chart-colored">
                  {{ $t('gridPageChartEditor.representation') }}
                  <template #right>
                    <chart-editor-setting-value :value="displayValue" />
                  </template>
                </m-card-item>
              </template>
            </aggregation-selector>
          </template>
          <!-- Metric chart: Data section -->
          <template v-if="effectiveChartType === gridPageChartType.metric">
            <div class="_section-header">
              {{ $t('gridPageChartEditor.metricSection') }}
            </div>
            <aggregation-selector
              v-model:value="yAxisGroupBy"
              :properties="properties"
            >
              <template #trigger="{ displayValue }">
                <m-card-item icon="corner-down-right">
                  {{ $t('gridPageChartEditor.whatToShow') }}
                  <template #right>
                    <chart-editor-setting-value :value="displayValue" />
                  </template>
                </m-card-item>
              </template>
            </aggregation-selector>
          </template>
          <template v-if="effectiveChartType !== gridPageChartType.metric && effectiveChartType !== gridPageChartType.table">
            <div class="_section-header">
              {{ $t('gridPageChartEditor.style') }}
            </div>
            <m-dropdown
              block
              match-trigger-width
              :title="$t('gridPageChartEditor.colorScheme')"
            >
              <m-card-item icon="palette">
                {{ $t('gridPageChartEditor.colorScheme') }}
                <template #right>
                  <chart-editor-setting-value :value="colorSchemeOptionsMap[colorScheme]?.text" />
                </template>
              </m-card-item>
              <template #overlay>
                <m-card
                  no-padding
                  list
                >
                  <m-card-item
                    v-for="option in colorSchemeOptions"
                    :key="option.value"
                    @click="colorScheme = option.value"
                  >
                    <div class="_color-scheme-item">
                      <span class="_color-scheme-label">{{ option.text }}</span>
                      <span
                        v-if="option.swatches.length"
                        class="_color-scheme-swatches"
                      >
                        <span
                          v-for="(swatch, i) in option.swatches"
                          :key="i"
                          class="_color-swatch"
                          :style="{ backgroundColor: swatch }"
                        />
                      </span>
                    </div>
                  </m-card-item>
                </m-card>
              </template>
            </m-dropdown>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import AggregationSelector from '@/components/custom-grid/AggregationSelector.vue';
import Breadcrumbs from '@/components/breadcrumbs/Breadcrumbs.vue';
import BucketSelector from '@/components/custom-grid/BucketSelector.vue';
import ChartEditorSettingValue from '@/components/custom-grid/ChartEditorSettingValue.vue';
import DraggableLayout from '@/components/custom-grid/chart/DraggableLayout.vue';
import FullScreenSpinner from 'shared/components/FullScreenSpinner.vue';
import GoalCycleSelector from '@/components/goal/cycle/GoalCycleSelector.vue';
import GridPageChart from '@/components/custom-grid/chart/GridPageChart.vue';
import GridPageChartDataTable from '@/components/custom-grid/GridPageChartDataTable.vue';
import GroupBySelector from '@/components/custom-grid/GroupBySelector.vue';
import ScopeFilter from '@/components/filter/ScopeFilter.vue';
import SimpleEditor from '@/components/SimpleEditor.vue';
import useAccountSettings from '@/composables/logged-in-user-account/account-settings';
import useChartDataQuery from '@/composables/chart-data-query';
import useConfirmDialog from '@/composables/confirm-dialog';
import useDebounce from 'shared/composables/debounce';
import useGoalProperty from '@/composables/property/goal-property';
import useGridPage from '@/composables/grid-page/grid-page';
import useGridPageTileChart from '@/composables/grid-page/chart/grid-page-tile-chart';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useSnackbar from '@/composables/snackbar';
import { CREATED_AT, MODIFIED_AT } from 'shared/api/query/constants';
import { TIME_SERIES_AGGREGATORS } from '@/lib/constants';
import { aggregatorType, chartGroupByOption, gridPageChartColorScheme, gridPageChartTimeMode, gridPageChartType, propertyType, timeSeriesInterval } from 'shared/constants.json';
import { buildIconFromEntity } from 'shared/lib/icon';
import { canFillEmptyValues, isEqualGridPageTileChart } from '@/composables/grid-page/chart/utils';
import { computed, ref, watch } from 'vue';
import { createPropsList } from '@/lib/props';
import { directProperties as directGoalActivityProperties } from '@/lib/goal-activity/properties';
import { directProperties as directUpdateProperties } from '@/lib/updates/properties';
import { getGradientPalette, mcColorSet } from '@/lib/charts/colors';
import { goalActivity as goalActivityConfig, goal as goalConfig, update as updateConfig } from 'shared/api/query/configs.json';
import { goalChartProperties } from '@/lib/goal/properties';
import { textByLang } from 'shared/lib/language';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  gridPageTile: {
    type: Object,
    required: true,
  },
  gridPage: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const breadcrumbItems = ref([
  {
    title: props.gridPage.title,
    icons: [{ value: buildIconFromEntity(props.gridPage) }],
    onClick: () => { emit('close'); },
  },
]);

const debounceSvc = useDebounce();
const gridPageService = useGridPage();

const getIconByType = (type) => {
  switch (type) {
    case gridPageChartType.bar:
      return 'horizontal-bar-chart';
    case gridPageChartType.column:
      return 'vertical-bar-chart';
    case gridPageChartType.line:
      return 'line-chart-colored';
    case gridPageChartType.pie:
      return 'pie-chart-colored';
    case gridPageChartType.metric:
      return 'number';
    case gridPageChartType.table:
      return 'chart-table';
    default:
      return '';
  }
};

function updateTitle(val) {
  debounceSvc.debounce(() => gridPageService.updateGridPageTile({ uid: props.gridPageTile.uid, title: val }), 500);
}

const descriptionEditMode = ref(false);
function updateDescription(val) {
  debounceSvc.debounce(() => gridPageService.updateGridPageTile({ uid: props.gridPageTile.uid, description: val }), 500);
}

const chartService = useGridPageTileChart();
const gridPageSvc = useGridPage();
const gridPageTileChart = computed(() => {
  const res = gridPageSvc.gridPageTileChart.value.find((e) => e.tile.uid === props.gridPageTile.uid);
  if (res === undefined) {
    return null;
  }

  return res;
});

const tablePosition = ref('center');

const snackbarService = useSnackbar();

const save = () => {
  chartService.updateSingle(toSave.value).then(() => {
    snackbarService.success(t('success.saved'));
  });
};
const confirmDialog = useConfirmDialog();

const handleClose = () => {
  if (isDirty.value) {
    confirmDialog.confirm({
      title: t('gridPageChartEditor.closePrompt'),
      okText: t('general.yesDiscard'),
      okType: 'warning',
      cancelText: t('general.cancel'),
      onOk: () => {
        init();
        emit('close');
      },
    });
    return;
  }
  emit('close');
};

const discard = () => {
  init();
};

const toSave = computed(() => ({
  uid: gridPageTileChart.value.uid,
  source: source.value,
  chartType: effectiveChartType.value,
  filter: filter.value,
  goalCycles: selectedCycles.value,
  groupBy: {
    uid: gridPageTileChart.value.groupBy.uid,
    ...groupByToStore.value,
    customBucket: {
      uid: gridPageTileChart.value.groupBy.customBucket.uid,
      ...customBucket.value,
    },
  },
  aggregationConfig: {
    uid: aggregationConfig.value.uid,
    aggregation: {
      uid: aggregationConfig.value.aggregation.uid,
      ...yAxisGroupBy.value,
    },
    stackOptions: {
      uid: aggregationConfig.value.stackOptions.uid,
      ...stackOptionsToStore.value,
      customBucket: {
        uid: gridPageTileChart.value.aggregationConfig.stackOptions.customBucket.uid,
        ...stackCustomBucket.value,
      },
    },
    timeSeriesInterval: selectedInterval.value,
  },
  excludeNullValues: excludeNullValues.value,
  colorScheme: colorScheme.value,
}));

const stackOptionsToStore = computed(() => {
  if ([gridPageChartType.pie, gridPageChartType.metric].includes(effectiveChartType.value)) {
    return { groupBy: chartGroupByOption.none };
  }
  if (isTimeSeriesMode.value) {
    return { groupBy: chartGroupByOption.none };
  }
  return stackOptions.value;
});

const groupByToStore = computed(() => {
  if (effectiveChartType.value === gridPageChartType.metric) {
    return { groupBy: chartGroupByOption.none };
  }
  if (isTimeSeriesMode.value) {
    return { groupBy: chartGroupByOption.none };
  }
  return xAxisGroupBy.value;
});

const aggregationConfig = computed(() => gridPageTileChart.value.aggregationConfig);

const maxLength = (str, max) => {
  if (str.length === 0) {
    return t('general.untitled');
  }
  if (str.length > max) {
    return `${str.slice(0, max)}...`;
  }
  return str;
};

const source = ref(goalConfig.model);

const { loggedInUserAccount } = useLoggedInUserAccount();

const filter = ref(null);

const { properties: goalProperties, statusProperty } = useGoalProperty();
const { accountSettings } = useAccountSettings();
const canGroupByUser = computed(() => accountSettings.value?.canGroupByUser ?? false);

const selectedCycles = ref([]);

const chartTypes = [gridPageChartType.column, gridPageChartType.bar, gridPageChartType.line, gridPageChartType.pie, gridPageChartType.metric, gridPageChartType.table];
const chartType = ref(gridPageChartType.bar);

const xAxisGroupBy = ref(null);
const customBucket = ref(null);
const stackCustomBucket = ref(null);
const properties = computed(() => {
  const isUserProperty = (prop) => prop.isProperty && prop.property?.type === propertyType.user;
  const filterUser = (list) => (canGroupByUser.value ? list : list.filter((p) => !isUserProperty(p)));

  switch (source.value) {
    case goalConfig.model:
      return filterUser([
        { isProperty: false, edge: CREATED_AT, edgeType: propertyType.date },
        { isProperty: false, edge: MODIFIED_AT, edgeType: propertyType.date },
        { isProperty: false, edge: goalConfig.edges.cachedCalculatedCurrent, edgeType: propertyType.number },
        { isProperty: false, edge: goalConfig.edges.goalCycle, edgeType: propertyType.options },
        ...goalProperties.value.map((e) => ({ isProperty: true, property: e })),
      ]);
    case updateConfig.model:
      return directUpdateProperties(t).map((prop) => ({
        isProperty: false,
        edge: prop.edgeName,
        edgeType: prop.type,
        label: prop.label,
      }));
    case goalActivityConfig.model:
      return directGoalActivityProperties(t).map((prop) => ({
        isProperty: false,
        edge: prop.edgeName,
        edgeType: prop.type,
        label: prop.label,
      }));
    default:
      return [];
  }
});

const yAxisGroupBy = ref(null);
const stackOptions = ref(null);
const excludeNullValues = ref(false);
const colorScheme = ref(gridPageChartColorScheme.automatic);
const selectedInterval = ref(timeSeriesInterval.weekly);

const SWATCH_COUNT = 5;
const colorSchemeOptions = [
  { text: t('gridPageChartEditor.colorSchemeAutomatic'), value: gridPageChartColorScheme.automatic, swatches: [] },
  { text: t('gridPageChartEditor.colorSchemeColorful'), value: gridPageChartColorScheme.colorful, swatches: mcColorSet.slice(0, SWATCH_COUNT) },
  ...[
    gridPageChartColorScheme.grey,
    gridPageChartColorScheme.brown,
    gridPageChartColorScheme.yellow,
    gridPageChartColorScheme.orange,
    gridPageChartColorScheme.red,
    gridPageChartColorScheme.pink,
    gridPageChartColorScheme.purple,
    gridPageChartColorScheme.blue,
    gridPageChartColorScheme.green,
  ].map((name) => ({
    text: t(`gridPageChartEditor.colorScheme${name.charAt(0).toUpperCase()}${name.slice(1)}`),
    value: name,
    swatches: getGradientPalette(name, SWATCH_COUNT),
  })),
];
const colorSchemeOptionsMap = Object.fromEntries(colorSchemeOptions.map((o) => [o.value, o]));
const showExcludeNullValues = computed(() => canFillEmptyValues(xAxisGroupBy.value));

const timeMode = ref(gridPageChartTimeMode.snapshot);

const isTimeSeriesMode = computed(() => timeMode.value === gridPageChartTimeMode.overTime);

const visibleChartTypes = computed(() => {
  if (isTimeSeriesMode.value) {
    return [gridPageChartType.line];
  }
  return chartTypes;
});

const categoryIsXAxis = computed(() => effectiveChartType.value !== gridPageChartType.bar);

const effectiveChartType = computed(() => {
  if (isTimeSeriesMode.value) {
    return gridPageChartType.line;
  }
  return chartType.value;
});

const timeModeOptions = [
  { text: t('gridPageChartEditor.snapshot'), value: gridPageChartTimeMode.snapshot },
  { text: t('gridPageChartEditor.overTime'), value: gridPageChartTimeMode.overTime },
];

const timeSeriesIntervalOptions = [
  { text: t('gridPageChartEditor.daily'), value: timeSeriesInterval.daily },
  { text: t('gridPageChartEditor.weekly'), value: timeSeriesInterval.weekly },
  { text: t('gridPageChartEditor.monthly'), value: timeSeriesInterval.monthly },
];

const toggleExcludeNullValues = () => {
  excludeNullValues.value = !excludeNullValues.value;
};

const { userLang } = useLoggedInUser();
const statusPropertyLabel = computed(() => (statusProperty.value ? textByLang(statusProperty.value.label, userLang.value) : 'Status'));

const timeSeriesMetricOptions = computed(() => [
  { text: t('gridPageChartEditor.progress'), value: aggregatorType.progressOverTime, icon: 'line-chart' },
  { text: t('gridPageChartEditor.statusScore', { property: statusPropertyLabel.value }), value: aggregatorType.statusScoreOverTime, icon: 'status' },
  { text: t('gridPageChartEditor.statusDistribution', { property: statusPropertyLabel.value }), value: aggregatorType.statusDistributionOverTime, icon: 'status' },
]);

const goalDefaultProps = computed(() => createPropsList({
  properties: goalProperties.value,
  directProperties: goalChartProperties(t),
  userLang: userLang.value,
  model: goalConfig.model,
}));

const updateDefaultProps = computed(() => createPropsList({
  properties: [],
  directProperties: directUpdateProperties(t),
  userLang: userLang.value,
  model: updateConfig.model,
}));

const goalActivityDefaultProps = computed(() => createPropsList({
  properties: [],
  directProperties: directGoalActivityProperties(t),
  userLang: userLang.value,
  model: goalActivityConfig.model,
}));

const filterProps = computed(() => {
  switch (source.value) {
    case goalConfig.model:
      return goalDefaultProps.value;
    case updateConfig.model:
      return updateDefaultProps.value;
    case goalActivityConfig.model:
      return goalActivityDefaultProps.value;
    default:
      return [];
  }
});

const sources = [
  {
    text: t('gridPageChartEditor.goal'),
    icon: 'compass',
    value: goalConfig.model,
  },
  {
    text: t('gridPageChartEditor.checkin'),
    icon: 'history',
    value: updateConfig.model,
  },
  {
    text: t('gridPageChartEditor.goalActivity'),
    icon: 'sync',
    value: goalActivityConfig.model,
  },
];

const sourceDisplayValue = computed(() => sources.find((s) => s.value === source.value)?.text);
const intervalDisplayValue = computed(() => timeSeriesIntervalOptions.find((o) => o.value === selectedInterval.value)?.text);
const timeSeriesMetricDisplayValue = computed(() => timeSeriesMetricOptions.value.find((o) => o.value === yAxisGroupBy.value?.aggregator)?.text);

const init = () => {
  source.value = gridPageTileChart.value.source;
  chartType.value = gridPageTileChart.value.chartType;
  filter.value = gridPageTileChart.value.filter;
  selectedCycles.value = gridPageTileChart.value.goalCycles;
  yAxisGroupBy.value = gridPageTileChart.value.aggregationConfig.aggregation;
  stackOptions.value = { ...gridPageTileChart.value.aggregationConfig.stackOptions, customBucket: undefined };
  xAxisGroupBy.value = { ...gridPageTileChart.value.groupBy, customBucket: undefined };
  customBucket.value = { ...gridPageTileChart.value.groupBy.customBucket };
  stackCustomBucket.value = { ...gridPageTileChart.value.aggregationConfig.stackOptions.customBucket };
  excludeNullValues.value = gridPageTileChart.value.excludeNullValues ?? false;
  colorScheme.value = gridPageTileChart.value.colorScheme ?? gridPageChartColorScheme.automatic;
  selectedInterval.value = gridPageTileChart.value.aggregationConfig.timeSeriesInterval ?? timeSeriesInterval.weekly;
  timeMode.value = TIME_SERIES_AGGREGATORS.includes(gridPageTileChart.value.aggregationConfig.aggregation.aggregator)
    ? gridPageChartTimeMode.overTime
    : gridPageChartTimeMode.snapshot;
};

const chartDataQuery = useChartDataQuery(toSave);

if (gridPageTileChart.value !== null) {
  init();
}

const findMatchingProperty = (currentValue, availableProperties) => {
  if (currentValue === null || currentValue === undefined) {
    return null;
  }
  return availableProperties.find((prop) => {
    if (currentValue.isProperty === true && prop.isProperty === true) {
      return currentValue.property?.uid === prop.property?.uid;
    }
    if (currentValue.isProperty === false && prop.isProperty === false) {
      return currentValue.edge === prop.edge;
    }
    return false;
  });
};

const getDefaultGroupBy = (availableProperties) => {
  const nonDateNumberProp = availableProperties.find((p) => {
    const type = p.isProperty === true ? p.property.type : p.edgeType;
    return type !== propertyType.date && type !== propertyType.number;
  });
  if (nonDateNumberProp !== undefined) {
    return { ...nonDateNumberProp, groupBy: chartGroupByOption.unique };
  }
  const firstProp = availableProperties[0];
  if (firstProp !== undefined) {
    const type = firstProp.isProperty === true ? firstProp.property.type : firstProp.edgeType;
    const groupBy = type === propertyType.date ? chartGroupByOption.day : chartGroupByOption.unique;
    return { ...firstProp, groupBy };
  }
  return { groupBy: chartGroupByOption.none };
};

watch(source, (newVal, oldVal) => {
  if (newVal === oldVal) {
    return;
  }
  filter.value = null;

  if (newVal !== goalConfig.model && isTimeSeriesMode.value) {
    timeMode.value = gridPageChartTimeMode.snapshot;
    yAxisGroupBy.value = { aggregator: aggregatorType.count };
  }

  // properties.value is already updated since source changed
  const newProperties = properties.value;

  const matchingXAxis = findMatchingProperty(xAxisGroupBy.value, newProperties);
  xAxisGroupBy.value = matchingXAxis !== null && matchingXAxis !== undefined
    ? { ...matchingXAxis, groupBy: xAxisGroupBy.value.groupBy }
    : getDefaultGroupBy(newProperties);

  const matchingYAxis = findMatchingProperty(yAxisGroupBy.value, newProperties);
  yAxisGroupBy.value = matchingYAxis !== null && matchingYAxis !== undefined
    ? { ...matchingYAxis, aggregator: yAxisGroupBy.value.aggregator }
    : { aggregator: aggregatorType.count };

  const matchingStack = findMatchingProperty(stackOptions.value, newProperties);
  stackOptions.value = matchingStack !== null && matchingStack !== undefined
    ? { ...matchingStack, groupBy: stackOptions.value.groupBy }
    : { groupBy: chartGroupByOption.none };
});

watch(timeMode, (newVal, oldVal) => {
  if (newVal === oldVal) {
    return;
  }
  if (newVal === gridPageChartTimeMode.overTime) {
    if (!TIME_SERIES_AGGREGATORS.includes(yAxisGroupBy.value?.aggregator)) {
      yAxisGroupBy.value = { aggregator: aggregatorType.progressOverTime };
    }
    chartType.value = gridPageChartType.line;
  } else if (TIME_SERIES_AGGREGATORS.includes(yAxisGroupBy.value?.aggregator)) {
    yAxisGroupBy.value = { aggregator: aggregatorType.count };
  }
});

const isDirty = computed(() => !isEqualGridPageTileChart(toSave.value, gridPageTileChart.value));
</script>

<style scoped lang="scss">
$top-bar-height: 6rem;

.grid-page-chart-editor {
  display: grid;
  grid-template-rows: $top-bar-height 1fr;
  height: var(--viewport-height-100);
  overflow: hidden;

  ._top {
    flex: 0 0 4rem;
    border-bottom: 1px solid $border-color;
    display: flex;
    align-items: center;
    padding: 0 2rem;

    ._left {
      display: flex;
      overflow: hidden;

      ._divider {
        margin: 0 .6rem;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    ._right {
      margin-left: auto;
      display: flex;
      gap: .4rem;
      flex-shrink: 0;
    }

    @media (max-width: $screen-size-md) {
      padding: 0 1rem;

      ._left {
        min-width: 0;

        ._breadcrumbs,
        ._divider,
        ._description {
          display: none;
        }

        ._title {
          font-weight: $font-weight-semibold;
        }
      }

      ._right {
        ._discard-btn {
          display: none;
        }
      }
    }
  }

  ._bottom {
    display: grid;
    grid-template-columns: 1fr 30rem;
    height: 100%;
    overflow: hidden;

    ._left {
      display: grid;
      overflow: hidden;

      ._draggable-layout {
        overflow: auto;
      }

      ._chart-container {
        height: 100%;
        overflow: hidden;
        display: flex;
        flex-direction: column;

        ._chart-content {
          height: 100%;
          width: 100%;
          display: flex;
          overflow: hidden;
        }

        &.-table {
          overflow: auto;

          ._chart-content {
            display: inline-table;
          }
        }
      }

      ._table-container {
        display: inline-table;
        min-width: 100%;
        padding-top: 2rem;
      }
    }

    ._right {
      border-left: 1px solid $border-color;
      overflow-y: auto;

      ._inner {
        padding: 1.2rem 0.8rem;
      }
    }

    ._time-mode-tabs {
      display: flex;
      border-bottom: 1px solid $border-color;

      .m-tab {
        flex: 1;
        justify-content: center;
      }
    }

    @media (max-width: $screen-size-md) {
      grid-template-columns: 1fr;

      ._left {
        display: none;
      }

      ._right {
        border-left: none;
      }
    }
  }

  ._section-header {
    font-size: $font-size-2;
    font-weight: $font-weight-semibold;
    color: $font-color-secondary;
    padding: 2rem 0 0.2rem 1.2rem;

    &:first-child {
      padding-top: 0;
    }
  }

  ._chart-type-selector {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 0.6rem 0.4rem 0.4rem 1.2rem;

    ._chart-btn {
      border: none;
      width: 4.2rem;
      height: 4.2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 0 0 1px $input-border-color;

      ._icon-wrapper {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &.-selected {
        box-shadow: 0 0 0 2px $primary-color;
      }

      &.-disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }
    }
  }

  ._user-grouping-hint {
    font-size: 1.2rem;
    margin: 0.8rem 1.2rem 0;
  }
}

._color-scheme-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: .8rem;

  ._color-scheme-label {
    flex: 1;
    white-space: nowrap;
  }

  ._color-scheme-swatches {
    display: flex;
    gap: .3rem;
    flex-shrink: 0;

    ._color-swatch {
      width: 1.6rem;
      height: 1.6rem;
      border-radius: .3rem;
    }
  }
}
</style>
